import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, Bot, User } from 'lucide-react'
import { get } from '~/lib/api'
import type { ConversationDetail as Conversation } from '~/lib/types'
import { formatAgo, rupees, shortDate } from '~/lib/format'
import { WAITING_LABELS } from '~/pages/Conversations'
import StageBadge from '~/components/StageBadge'
import Spinner from '~/components/Spinner'
import { LIVE_REFETCH_MS } from '~/lib/queryClient'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'

export default function ConversationDetail() {
  const { conversationId } = useParams({ from: '/app/conversations/$conversationId' })
  const search = useSearch({ from: '/app/conversations/$conversationId' })

  const { data, isPending, isError } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => get<Conversation>(`/api/v1/conversations/${conversationId}`),
    refetchInterval: LIVE_REFETCH_MS,
  })

  const latestRef = useThreadPinnedToLatest(conversationId, data?.messages.length ?? 0)

  if (isPending) {
    return (
      <div className="grid min-h-[50vh] place-items-center">
        <Spinner label="Loading conversation" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-start gap-4">
        <p role="alert" className="text-sm text-critical">
          That conversation doesn&apos;t exist.
        </p>
        <BackLink search={search} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <BackLink search={search} />
          <h1 className="mt-3 flex flex-wrap items-center gap-3 text-2xl font-semibold tracking-tight">
            {data.student}
            <StageBadge stage={data.stage} />
          </h1>
          <p className="mt-1 text-sm text-ink-2">
            {data.course} &middot; last activity {formatAgo(data.minutes_ago)}
          </p>
        </div>
      </div>

      {data.escalated && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface p-4">
          <AlertTriangle className="size-4 shrink-0 text-warning" aria-hidden="true" />
          <p className="flex-1 text-sm">
            <span className="font-medium">Escalated to a person</span>
            <span className="text-ink-2"> — the agent stopped replying and is waiting for you.</span>
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-sm">Thread</CardTitle>
          </CardHeader>

          <CardContent>
            <ol className="flex flex-col gap-4">
              {data.messages.map((message, index) => {
                const fromAgent = message.role === 'agent'

                return (
                  <li
                    key={index}
                    className={`flex gap-3 ${fromAgent ? '' : 'flex-row-reverse'}`}
                  >
                    <span
                      className={`grid size-7 shrink-0 place-items-center rounded-full ${
                        fromAgent ? 'bg-brand text-white' : 'bg-surface-2 text-ink-2'
                      }`}
                      aria-hidden="true"
                    >
                      {fromAgent ? <Bot className="size-4" /> : <User className="size-4" />}
                    </span>

                    <div className={`max-w-[75%] ${fromAgent ? '' : 'text-right'}`}>
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-sm ${
                          fromAgent
                            ? 'rounded-tl-sm bg-surface-2 text-ink'
                            : 'rounded-tr-sm bg-brand text-white'
                        }`}
                      >
                        {message.body}
                      </div>
                      <p className="mt-1 text-xs text-faint">
                        {fromAgent ? 'Agent' : data.student.split(' ')[0]} &middot;{' '}
                        {formatAgo(message.minutes_ago)}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ol>

            <div ref={latestRef} aria-hidden="true" />

            <p className="mt-6 border-t border-line pt-4 text-xs text-faint">
              Replying from here arrives with the messaging integration — the agent sends over
              WhatsApp today.
            </p>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Student</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[6.5rem_1fr] gap-y-2.5 text-sm">
                <Detail label="Email">{data.student_email}</Detail>
                <Detail label="Phone">{data.student_phone}</Detail>
                <Detail label="Registered">{shortDate(data.registered_on)}</Detail>
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Conversation</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[6.5rem_1fr] gap-y-2.5 text-sm">
                <Detail label="Course">{data.course}</Detail>
                <Detail label="Fee">{rupees(data.course_fee)}</Detail>
                <Detail label="Waiting on">{WAITING_LABELS[data.waiting_on]}</Detail>
                <Detail label="Messages">{data.messages.length}</Detail>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

/*
  A thread reads newest-last, so opening one should land on the newest message
  rather than on history the reader has already seen, and a message arriving
  while they watch should bring itself into view.

  Except when they have scrolled up to read something. Yanking the page out
  from under someone mid-sentence is worse than making them scroll, so this
  only follows along while the end of the thread is still in front of them.

  "In front of them" is measured against the end of the thread, not the bottom
  of the page: the cards beside the thread run lower than it does, so someone
  reading the newest message is nowhere near the bottom of the document.
*/
const PINNED_SLACK_PX = 120

function useThreadPinnedToLatest(conversationId: string, messageCount: number) {
  const latestRef = useRef<HTMLDivElement>(null)
  const pinned = useRef(true)
  const seen = useRef(0)

  useEffect(() => {
    const onScroll = () => {
      const end = latestRef.current
      if (!end) return

      // Scrolling up pushes the end of the thread down the viewport.
      pinned.current = end.getBoundingClientRect().top <= window.innerHeight + PINNED_SLACK_PX
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // A different conversation is a fresh thread: jump, do not glide, and treat
  // the reader as pinned again.
  useEffect(() => {
    pinned.current = true
    seen.current = 0
  }, [conversationId])

  useEffect(() => {
    if (messageCount === 0 || !pinned.current) return

    const opening = seen.current === 0
    seen.current = messageCount

    latestRef.current?.scrollIntoView({ block: 'end', behavior: opening ? 'instant' : 'smooth' })
  }, [conversationId, messageCount])

  return latestRef
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-faint">{label}</dt>
      <dd className="min-w-0 truncate">{children}</dd>
    </>
  )
}

function BackLink({ search }: { search: { stage: string; escalated: boolean } }) {
  return (
    <Button asChild variant="outline" size="sm">
      <Link to="/conversations" search={search as never}>
        <ArrowLeft className="size-3.5" aria-hidden="true" />
        All conversations
      </Link>
    </Button>
  )
}
